import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ReactECharts from 'echarts-for-react';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import SafeIcon from '../../common/SafeIcon';
import { submitIntelligenceReviewLead } from '../../services/leadService';
import api from '../../services/api';

const PLATFORM_GRADIENTS = {
  meta: 'from-blue-600/20 via-black to-indigo-900/20',
  google: 'from-emerald-600/20 via-black to-teal-900/20',
  tiktok: 'from-zinc-600/20 via-black to-zinc-900/20',
  linkedin: 'from-sky-700/20 via-black to-blue-900/20'
};

const mapCompetitor = (c) => ({
  id: c.id,
  name: c.name,
  industry: c.industry,
  description: c.description || 'Tracked competitor in the Client Intelligence Portal.',
  logoColor: c.logo_color,
  activeAdsCount: Number(c.active_ads_count || 0),
  totalAdsTracked: Number(c.total_ads_tracked || 0)
});

const mapAd = (a) => ({
  id: a.id,
  competitorId: a.competitor_id,
  platform: a.platform,
  format: a.format,
  status: a.status,
  firstSeen: a.first_seen,
  lastSeen: a.last_seen,
  longevityDays: Number(a.longevity_days || 0),
  title: a.title,
  copy: a.copy || '',
  landingPageUrl: a.landing_page_url,
  ctaText: a.cta_text,
  thumbnailGradient: PLATFORM_GRADIENTS[a.platform] || 'from-zinc-800/20 via-black to-zinc-900/20',
  analysis: {
    hook: a.analysis_hook || 'No analysis recorded yet.',
    angle: a.analysis_angle || 'No analysis recorded yet.',
    funnel: a.analysis_funnel || 'No analysis recorded yet.',
    maxmarkAdvisory: a.analysis_advisory || 'No advisory recorded yet.'
  }
});

const PLATFORM_BADGE_COLORS = {
  meta: 'bg-blue-500/10 text-blue-400 border border-blue-500/20',
  google: 'bg-green-500/10 text-green-400 border border-green-500/20',
  tiktok: 'bg-purple-500/10 text-purple-400 border border-purple-500/20',
  linkedin: 'bg-sky-500/10 text-sky-400 border border-sky-500/20'
};

const formatRelativeTime = (isoDate) => {
  const diffMs = Date.now() - new Date(isoDate.replace(' ', 'T') + 'Z').getTime();
  const diffMins = Math.round(diffMs / 60000);
  if (diffMins < 60) return `${Math.max(diffMins, 1)} min ago`;
  const diffHours = Math.round(diffMins / 60);
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  const diffDays = Math.round(diffHours / 24);
  return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
};

const mapAlert = (a) => ({
  id: a.id,
  competitorId: a.competitor_id,
  platform: a.platform,
  text: a.text,
  timestamp: formatRelativeTime(a.created_at),
  badgeColor: PLATFORM_BADGE_COLORS[a.platform] || 'bg-zinc-500/10 text-zinc-400 border border-zinc-500/20'
});

const getPlatformShareData = (ads, competitorId = null) => {
  const filtered = competitorId ? ads.filter(a => a.competitorId === competitorId) : ads;
  const counts = { meta: 0, google: 0, tiktok: 0, linkedin: 0 };
  filtered.forEach(ad => {
    if (counts[ad.platform] !== undefined) {
      counts[ad.platform]++;
    }
  });

  return [
    { name: 'Meta (FB/IG)', value: counts.meta },
    { name: 'Google Ads', value: counts.google },
    { name: 'TikTok Ads', value: counts.tiktok },
    { name: 'LinkedIn Ads', value: counts.linkedin }
  ].filter(item => item.value > 0);
};

const {
  FiSliders,
  FiEye,
  FiFileText,
  FiPlusCircle,
  FiSettings,
  FiLogOut,
  FiSearch,
  FiFilter,
  FiArrowRight,
  FiCheckCircle,
  FiAlertCircle,
  FiDownload,
  FiLayout,
  FiClock,
  FiGlobe,
  FiCalendar,
  FiGrid,
  FiList,
  FiTag,
  FiCopy,
  FiExternalLink,
  FiMenu,
  FiX
} = FiIcons;

export default function Dashboard() {
  const navigate = useNavigate();
  const [clientUser, setClientUser] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);
  const [competitors, setCompetitors] = useState([]);
  const [ads, setAds] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [preferences, setPreferences] = useState({ new_campaigns: true, longevity_alerts: true, monthly_briefing: true });

  const [activeTab, setActiveTab] = useState('overview'); // overview, swipe, reports, request, settings
  const [selectedCompetitorId, setSelectedCompetitorId] = useState('all');

  // Swipe File Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [formatFilter, setFormatFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('longevity'); // longevity, newest, oldest
  
  // Selected Ad for Detail Panel
  const [selectedAd, setSelectedAd] = useState(null);
  const [adTab, setAdTab] = useState('copy'); // copy, funnel, advisory

  // Whitelabel Report Customizer State
  const [reportCompetitorId, setReportCompetitorId] = useState(null);
  const [agencyName, setAgencyName] = useState('Maxmark Agency');
  const [reportTitle, setReportTitle] = useState('Competitor Advertising Audit');
  const [brandColor, setBrandColor] = useState('#2F6BFF');
  const [customComments, setCustomComments] = useState(
    'Key Observation: Monitored competitors are heavily shifting search and display budgets toward specialized, high-intent products (e.g. POS Lending and Creator Storefronts). Direct acquisition costs for generic fintech keywords are rising. We recommend positioning our counter-campaigns around tailored business onboarding.'
  );
  const [uploadedLogo, setUploadedLogo] = useState(null);
  
  // Request Monitor Form State
  const [reqName, setReqName] = useState('');
  const [reqWebsite, setReqWebsite] = useState('');
  const [reqCompetitors, setReqCompetitors] = useState('');
  const [reqConcern, setReqConcern] = useState('');
  const [reqChannels, setReqChannels] = useState([]);
  const [reqSubmitLoading, setReqSubmitLoading] = useState(false);
  const [reqSubmitSuccess, setReqSubmitSuccess] = useState(false);
  const [pendingRequests, setPendingRequests] = useState([]);

  // Mobile Menu State
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Copy success indicator
  const [copiedId, setCopiedId] = useState(null);

  const handleTogglePreference = async (key) => {
    const updated = { ...preferences, [key]: !preferences[key] };
    setPreferences(updated); // optimistic
    try {
      await api.updateClientPreferences(updated);
    } catch (err) {
      console.error('Error saving preference:', err);
      setPreferences(preferences); // roll back
    }
  };

  // Verify the client session, then load the workspace's data
  useEffect(() => {
    let cancelled = false;

    const loadWorkspace = async () => {
      try {
        const verifyRes = await api.clientVerify();
        if (cancelled) return;
        setClientUser(verifyRes.user);

        const [competitorsRes, adsRes, alertsRes, requestsRes, preferencesRes] = await Promise.all([
          api.getClientCompetitors(),
          api.getClientAds(),
          api.getClientAlerts(),
          api.getClientRequests(),
          api.getClientPreferences()
        ]);
        if (cancelled) return;

        const mappedCompetitors = (competitorsRes.competitors || []).map(mapCompetitor);
        setCompetitors(mappedCompetitors);
        setAds((adsRes.ads || []).map(mapAd));
        setAlerts((alertsRes.alerts || []).map(mapAlert));
        setPendingRequests(requestsRes.requests || []);
        setPreferences(preferencesRes.preferences);
        setReportCompetitorId(mappedCompetitors[0]?.id || null);
      } catch (err) {
        if (!cancelled) navigate('/login');
      } finally {
        if (!cancelled) setDataLoading(false);
      }
    };

    loadWorkspace();
    return () => { cancelled = true; };
  }, [navigate]);

  // Filter Ads
  const getFilteredAds = () => {
    let result = [...ads];

    // Competitor filter
    if (selectedCompetitorId !== 'all') {
      result = result.filter(ad => ad.competitorId === selectedCompetitorId);
    }
    
    // Platform filter
    if (platformFilter !== 'all') {
      result = result.filter(ad => ad.platform === platformFilter);
    }
    
    // Format filter
    if (formatFilter !== 'all') {
      result = result.filter(ad => ad.format === formatFilter);
    }
    
    // Status filter
    if (statusFilter !== 'all') {
      result = result.filter(ad => ad.status === statusFilter);
    }
    
    // Search query filter
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      result = result.filter(ad => 
        ad.title.toLowerCase().includes(query) || 
        ad.copy.toLowerCase().includes(query)
      );
    }
    
    // Sorting
    result.sort((a, b) => {
      if (sortBy === 'longevity') {
        return b.longevityDays - a.longevityDays;
      }
      if (sortBy === 'newest') {
        return new Date(b.firstSeen) - new Date(a.firstSeen);
      }
      if (sortBy === 'oldest') {
        return new Date(a.firstSeen) - new Date(b.firstSeen);
      }
      return 0;
    });
    
    return result;
  };

  const filteredAds = getFilteredAds();

  // Handle Logo Upload
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedLogo(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Copy ad copy to clipboard
  const handleCopyText = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Handle Request Monitor Submit
  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    if (!reqName || !reqWebsite) return;

    setReqSubmitLoading(true);
    setReqSubmitSuccess(false);

    try {
      // Client identity (name/company/email) is attached server-side from
      // the authenticated client token - see routes/public_leads.php.
      await submitIntelligenceReviewLead({
        marketing_challenge: reqConcern || 'Client Portal competitor track request',
        primary_market: clientUser?.company_name || '',
        competitors: reqName,
        competitor_websites: reqWebsite,
        monitoring_platforms: reqChannels,
        competitive_concern: `Request to monitor ${reqName} (${reqWebsite}).${reqCompetitors ? ` Related competitors: ${reqCompetitors}.` : ''}`,
        current_advertising_platforms: [],
        campaign_objective: 'Competitor Surveillance',
        desired_result: 'Swipe file and creative analysis',
        preferred_contact_method: 'Email'
      });

      const requestsRes = await api.getClientRequests();
      setPendingRequests(requestsRes.requests || []);

      setReqName('');
      setReqWebsite('');
      setReqCompetitors('');
      setReqConcern('');
      setReqChannels([]);
      setReqSubmitSuccess(true);
    } catch (err) {
      console.error('Error submitting tracker request:', err);
      setReqName('');
      setReqWebsite('');
      setReqSubmitSuccess(true);
    } finally {
      setReqSubmitLoading(false);
    }
  };

  // ECharts Config for Platform Share
  const getChartOptions = (competitorId = null) => {
    const data = getPlatformShareData(ads, competitorId === 'all' ? null : competitorId);
    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} ads ({d}%)',
        backgroundColor: '#0F111A',
        borderColor: '#242832',
        textStyle: { color: '#F7F8FA', fontSize: 11 }
      },
      legend: {
        orient: 'horizontal',
        bottom: '0%',
        left: 'center',
        textStyle: { color: '#A7ADB8', fontSize: 10 },
        icon: 'circle'
      },
      series: [
        {
          name: 'Ad Platform Share',
          type: 'pie',
          radius: ['45%', '70%'],
          center: ['50%', '42%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 8,
            borderColor: '#050506',
            borderWidth: 2
          },
          label: {
            show: false,
            position: 'center'
          },
          emphasis: {
            label: {
              show: true,
              fontSize: 12,
              fontWeight: 'bold',
              color: '#F7F8FA'
            }
          },
          labelLine: {
            show: false
          },
          data: data.map((item, idx) => {
            const colors = ['#2F6BFF', '#4285F4', '#00F2FE', '#0077B5'];
            return {
              ...item,
              itemStyle: { color: colors[idx % colors.length] }
            };
          })
        }
      ]
    };
  };

  if (dataLoading) {
    return (
      <div className="min-h-screen bg-[#030509] flex items-center justify-center">
        <p className="text-zinc-500 text-sm">Verifying workspace credentials...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030509] text-[#F7F8FA] flex flex-col font-sans">
      {/* Stylesheet for custom Print Formatting */}
      <style dangerouslySetInnerHTML={{__html: `
        @media print {
          body * {
            visibility: hidden !important;
          }
          #print-area, #print-area * {
            visibility: visible !important;
          }
          #print-area {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            background: white !important;
            color: black !important;
            box-shadow: none !important;
            border: none !important;
          }
          .print-hidden {
            display: none !important;
          }
        }
      `}} />

      {/* Top Navbar */}
      <header className="h-16 border-b border-[#242832] bg-[#050506] flex items-center justify-between px-6 sticky top-0 z-50 print-hidden">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => setMobileSidebarOpen(true)}
            className="md:hidden p-2 text-[#A7ADB8] hover:text-white"
          >
            <FiMenu className="w-5 h-5" />
          </button>
          <Link to="/" className="text-lg font-bold tracking-tighter text-[#F7F8FA]">
            MAXMARK<span className="text-[#2F6BFF]">.</span>
          </Link>
          <span className="h-4 w-px bg-[#242832] mx-2 hidden md:block"></span>
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-500 hidden md:block">Client Intelligence Portal</span>
        </div>

        <div className="flex items-center gap-4">
          <div className="bg-emerald-600/10 border border-emerald-500/20 text-emerald-400 text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
            {clientUser?.company_name}
          </div>
          <button
            onClick={() => {
              localStorage.removeItem('maxmark_client_token');
              navigate('/login');
            }}
            className="text-[#A7ADB8] hover:text-white flex items-center gap-2 text-xs font-bold uppercase tracking-wider"
          >
            <FiLogOut className="w-4 h-4" />
            <span className="hidden md:inline">Sign Out</span>
          </button>
        </div>
      </header>

      {/* Main Container */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar Left */}
        <aside className="w-64 border-r border-[#242832] bg-[#050506] flex-shrink-0 hidden md:flex flex-col justify-between p-6 print-hidden">
          <div className="space-y-8">
            <div className="px-2">
              <h2 className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-1">Active Workspace</h2>
              <p className="text-sm font-semibold text-white truncate">Global Competitor Tracker</p>
            </div>

            <nav className="space-y-1.5">
              {[
                { id: 'overview', icon: FiLayout, label: 'Workspace Overview' },
                { id: 'swipe', icon: FiEye, label: 'Ad Swipe Files' },
                { id: 'reports', icon: FiFileText, label: 'Whitelabel Reports' },
                { id: 'request', icon: FiPlusCircle, label: 'Request Tracker' },
                { id: 'settings', icon: FiSettings, label: 'Portal Settings' },
              ].map(item => (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveTab(item.id);
                    setSelectedAd(null);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-bold uppercase tracking-widest transition-all ${
                    activeTab === item.id 
                      ? 'bg-[#2F6BFF] text-white shadow-lg shadow-blue-600/20' 
                      : 'text-[#A7ADB8] hover:bg-zinc-900 hover:text-white'
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="border-t border-[#242832] pt-6 px-2 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-blue-600 to-indigo-800 flex items-center justify-center font-bold text-xs text-white uppercase">
                {(clientUser?.full_name || '?').slice(0, 2)}
              </div>
              <div className="min-w-0">
                <p className="text-xs font-bold text-white truncate">{clientUser?.full_name}</p>
                <p className="text-[10px] text-zinc-500 truncate">{clientUser?.email}</p>
              </div>
            </div>
          </div>
        </aside>

        {/* Mobile Sidebar Modal */}
        <AnimatePresence>
          {mobileSidebarOpen && (
            <div className="fixed inset-0 z-50 md:hidden flex print-hidden">
              {/* Overlay */}
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setMobileSidebarOpen(false)}
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              />

              {/* Sidebar Content */}
              <motion.div 
                initial={{ x: '-100%' }}
                animate={{ x: 0 }}
                exit={{ x: '-100%' }}
                transition={{ type: 'tween', duration: 0.3 }}
                className="relative w-64 bg-[#050506] border-r border-[#242832] h-full flex flex-col justify-between p-6 z-10"
              >
                <button 
                  onClick={() => setMobileSidebarOpen(false)}
                  className="absolute top-4 right-4 p-2 text-zinc-500 hover:text-white"
                >
                  <FiX className="w-5 h-5" />
                </button>

                <div className="space-y-8 mt-4">
                  <div className="px-2">
                    <h2 className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-1">Active Workspace</h2>
                    <p className="text-sm font-semibold text-white">Global Competitor Tracker</p>
                  </div>

                  <nav className="space-y-1">
                    {[
                      { id: 'overview', icon: FiLayout, label: 'Overview' },
                      { id: 'swipe', icon: FiEye, label: 'Ad Swipe Files' },
                      { id: 'reports', icon: FiFileText, label: 'Whitelabel Reports' },
                      { id: 'request', icon: FiPlusCircle, label: 'Request Tracker' },
                      { id: 'settings', icon: FiSettings, label: 'Portal Settings' },
                    ].map(item => (
                      <button
                        key={item.id}
                        onClick={() => {
                          setActiveTab(item.id);
                          setSelectedAd(null);
                          setMobileSidebarOpen(false);
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-bold uppercase tracking-widest transition-all ${
                          activeTab === item.id 
                            ? 'bg-[#2F6BFF] text-white' 
                            : 'text-[#A7ADB8] hover:bg-zinc-900 hover:text-white'
                        }`}
                      >
                        <item.icon className="w-4 h-4" />
                        {item.label}
                      </button>
                    ))}
                  </nav>
                </div>

                <div className="border-t border-[#242832] pt-6 px-2">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-blue-600 to-indigo-800 flex items-center justify-center font-bold text-xs text-white uppercase">
                      {(clientUser?.full_name || '?').slice(0, 2)}
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-bold text-white truncate">{clientUser?.full_name}</p>
                      <p className="text-[10px] text-zinc-500 truncate">{clientUser?.email}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Dashboard Main Workspace Viewport */}
        <main className="flex-1 overflow-y-auto bg-[#030509] p-6 md:p-8 flex flex-col gap-8">
          
          {/* TAB 1: WORKSPACE OVERVIEW */}
          {activeTab === 'overview' && (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-8"
            >
              {/* Header section */}
              <div className="flex justify-between items-start md:items-center flex-col md:flex-row gap-4">
                <div>
                  <h1 className="text-3xl font-serif font-bold text-white">Client Intelligence Dashboard</h1>
                  <p className="text-xs text-zinc-500 mt-1">Surveillance and creative analysis of active monitors.</p>
                </div>
                <button
                  onClick={() => setActiveTab('swipe')}
                  className="bg-[#2F6BFF] hover:bg-[#1a54ff] text-white px-5 py-3 rounded-xl text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition-all active:scale-95 shadow-md shadow-blue-600/15"
                >
                  <FiSearch className="w-4 h-4" />
                  Search Swipe File
                </button>
              </div>

              {/* Metrics Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { label: 'Monitored Competitors', value: competitors.length, sub: 'Active and tracking', icon: FiGlobe, color: 'from-blue-600/10 to-blue-900/5 border-blue-500/10 text-blue-400' },
                  { label: 'Total Tracked Ads', value: ads.length, sub: 'Archived permanently', icon: FiEye, color: 'from-cyan-600/10 to-cyan-900/5 border-cyan-500/10 text-cyan-400' },
                  { label: 'Active Campaigns', value: ads.filter(a => a.status === 'active').length, sub: 'Currently live', icon: FiCheckCircle, color: 'from-green-600/10 to-green-900/5 border-green-500/10 text-green-400' },
                  { label: 'Max Ad Longevity', value: `${ads.length ? Math.max(...ads.map(a => a.longevityDays)) : 0} Days`, sub: 'Ad creative scale indicator', icon: FiClock, color: 'from-yellow-600/10 to-yellow-900/5 border-yellow-500/10 text-yellow-400' },
                ].map((m, idx) => (
                  <div key={idx} className={`bg-gradient-to-br ${m.color} border rounded-2xl p-6 flex items-center justify-between shadow-xl`}>
                    <div>
                      <p className="text-zinc-500 text-[10px] font-bold uppercase tracking-wider">{m.label}</p>
                      <h3 className="text-3xl font-bold mt-2 text-white">{m.value}</h3>
                      <p className="text-zinc-500 text-xs mt-1 font-light">{m.sub}</p>
                    </div>
                    <div className="p-3 bg-zinc-950/60 rounded-xl border border-[#242832]">
                      <m.icon className="w-6 h-6" />
                    </div>
                  </div>
                ))}
              </div>

              {/* Two Column Layout: Chart & Alerts */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* Chart Box */}
                <div className="lg:col-span-7 bg-[#050506] border border-[#242832] rounded-3xl p-6 flex flex-col justify-between shadow-2xl h-[420px]">
                  <div>
                    <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-1">Platform Share distribution</h3>
                    <p className="text-xs text-zinc-500 font-light">Breakdown of archived competitor ads across social and search channels.</p>
                  </div>
                  <div className="flex-1 relative w-full h-[260px] mt-4">
                    <ReactECharts 
                      option={getChartOptions('all')} 
                      style={{ height: '100%', width: '100%' }}
                    />
                  </div>
                </div>

                {/* Alerts Log Box */}
                <div className="lg:col-span-5 bg-[#050506] border border-[#242832] rounded-3xl p-6 flex flex-col justify-between shadow-2xl h-[420px]">
                  <div>
                    <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-1">Competitor Intel Alerts</h3>
                    <p className="text-xs text-zinc-500 font-light">Real-time signals detected across monitoring targets.</p>
                  </div>
                  
                  <div className="flex-1 overflow-y-auto mt-6 space-y-4 pr-1">
                    {alerts.length === 0 && (
                      <p className="text-xs text-zinc-600 font-light">No activity yet. Alerts appear here as new competitor creative is tracked.</p>
                    )}
                    {alerts.map(alert => {
                      const comp = competitors.find(c => c.id === alert.competitorId);
                      return (
                        <div key={alert.id} className="p-4 bg-zinc-950 rounded-2xl border border-zinc-900 flex gap-4 items-start">
                          <div className={`px-2.5 py-1 text-[9px] font-bold uppercase rounded-md ${alert.badgeColor} shrink-0 mt-0.5`}>
                            {alert.platform}
                          </div>
                          <div className="space-y-1">
                            <p className="text-xs text-zinc-300 font-light leading-relaxed">
                              <span className="font-bold text-white mr-1">{comp?.name}:</span>
                              {alert.text}
                            </p>
                            <p className="text-[10px] text-zinc-500 flex items-center gap-1">
                              <FiClock className="w-3 h-3" />
                              {alert.timestamp}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Brand Monitor List */}
              <div className="bg-[#050506] border border-[#242832] rounded-3xl p-6 shadow-2xl">
                <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-6">Monitored Brand Status</h3>

                {competitors.length === 0 && (
                  <p className="text-xs text-zinc-600 font-light">No competitors assigned to your workspace yet. Use "Request Tracker" to ask Maxmark to add one.</p>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
                  {competitors.map(comp => (
                    <div 
                      key={comp.id} 
                      onClick={() => {
                        setSelectedCompetitorId(comp.id);
                        setActiveTab('swipe');
                      }}
                      className="bg-zinc-950 border border-zinc-900 hover:border-zinc-700 rounded-2xl p-6 flex flex-col justify-between cursor-pointer transition-all hover:-translate-y-1 shadow-md group"
                    >
                      <div>
                        <div className="flex justify-between items-start mb-4">
                          <div className={`px-3 py-1.5 rounded-lg bg-gradient-to-br ${comp.logoColor} text-white text-xs font-bold tracking-widest uppercase`}>
                            {comp.name}
                          </div>
                          <span className="text-[9px] uppercase font-bold tracking-widest text-zinc-500 bg-zinc-900 border border-zinc-800 px-2 py-1 rounded">
                            {comp.industry}
                          </span>
                        </div>
                        <p className="text-xs text-zinc-400 font-light leading-relaxed line-clamp-3">
                          {comp.description}
                        </p>
                      </div>

                      <div className="mt-6 pt-4 border-t border-zinc-900 flex justify-between items-center">
                        <div>
                          <p className="text-[9px] font-bold uppercase text-zinc-500">Active Swipe File</p>
                          <p className="text-sm font-bold text-white mt-1">{comp.activeAdsCount} Active Ads <span className="text-zinc-600 text-xs">/ {comp.totalAdsTracked} total</span></p>
                        </div>
                        <div className="w-8 h-8 rounded-full bg-zinc-900 group-hover:bg-[#2F6BFF] flex items-center justify-center transition-colors">
                          <FiArrowRight className="w-4 h-4 text-[#A7ADB8] group-hover:text-white" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* TAB 2: COMPETITOR SWIPE FILES */}
          {activeTab === 'swipe' && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex-1 flex flex-col lg:flex-row gap-6 overflow-hidden min-h-0"
            >
              {/* Sub Sidebar: Monitor Brands */}
              <div className="w-full lg:w-60 flex-shrink-0 flex flex-col gap-4">
                <div className="px-2">
                  <h3 className="text-xs font-bold text-zinc-500 uppercase tracking-widest">Monitored Brands</h3>
                  <p className="text-[10px] text-zinc-600 font-light mt-1">Select a brand to view its creative archive.</p>
                </div>
                
                <div className="flex flex-row lg:flex-col gap-1.5 overflow-x-auto lg:overflow-x-visible pb-2 lg:pb-0 scrollbar-none">
                  <button
                    onClick={() => setSelectedCompetitorId('all')}
                    className={`px-4 py-3 rounded-xl text-left text-xs font-bold uppercase tracking-widest shrink-0 transition-all ${
                      selectedCompetitorId === 'all'
                        ? 'bg-zinc-900 border border-zinc-800 text-white shadow'
                        : 'text-[#A7ADB8] hover:bg-zinc-950 border border-transparent'
                    }`}
                  >
                    All Monitored Ads ({ads.length})
                  </button>
                  {competitors.map(comp => (
                    <button
                      key={comp.id}
                      onClick={() => setSelectedCompetitorId(comp.id)}
                      className={`px-4 py-3 rounded-xl text-left text-xs font-bold uppercase tracking-widest shrink-0 transition-all flex justify-between items-center ${
                        selectedCompetitorId === comp.id
                          ? 'bg-zinc-900 border border-zinc-800 text-white shadow'
                          : 'text-[#A7ADB8] hover:bg-zinc-950 border border-transparent'
                      }`}
                    >
                      <span>{comp.name}</span>
                      <span className="text-[9px] bg-zinc-900 border border-zinc-800 text-zinc-500 px-1.5 py-0.5 rounded ml-2">
                        {comp.activeAdsCount}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Main Swipe File Feed */}
              <div className="flex-grow flex flex-col gap-6 overflow-y-auto pr-1">
                {/* Search & Filters Card */}
                <div className="bg-[#050506] border border-[#242832] rounded-3xl p-5 shadow-xl flex flex-col gap-4">
                  <div className="flex flex-col md:flex-row gap-4 items-center">
                    {/* Search Bar */}
                    <div className="relative flex-grow w-full">
                      <FiSearch className="absolute left-4 top-3.5 text-zinc-500 w-4 h-4" />
                      <input
                        type="text"
                        className="w-full bg-[#0B0C0F] border border-[#242832] rounded-xl pl-11 pr-4 py-3 text-xs placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-[#2F6BFF] focus:border-transparent transition-all"
                        placeholder="Search keyword in copy or headline..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                      />
                    </div>

                    {/* Platform Select */}
                    <div className="flex gap-1.5 p-1 bg-[#0B0C0F] border border-[#242832] rounded-xl w-full md:w-auto shrink-0 overflow-x-auto">
                      {['all', 'meta', 'google', 'tiktok', 'linkedin'].map(p => (
                        <button
                          key={p}
                          onClick={() => setPlatformFilter(p)}
                          className={`px-3.5 py-2 rounded-lg text-[10px] font-bold uppercase tracking-widest transition-all ${
                            platformFilter === p 
                              ? 'bg-blue-600 text-white' 
                              : 'text-zinc-400 hover:text-white'
                          }`}
                        >
                          {p === 'all' ? 'All Platforms' : p}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Filter Secondary Row */}
                  <div className="flex flex-wrap justify-between items-center gap-4 pt-2 border-t border-[#242832]">
                    <div className="flex flex-wrap gap-4 items-center">
                      {/* Format Filter */}
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] uppercase font-bold text-zinc-500">Format:</span>
                        <select
                          className="bg-[#0B0C0F] border border-[#242832] rounded-lg px-3 py-1.5 text-xs text-zinc-300 focus:outline-none"
                          value={formatFilter}
                          onChange={e => setFormatFilter(e.target.value)}
                        >
                          <option value="all">All Formats</option>
                          <option value="video">Video Ads</option>
                          <option value="image">Image Ads</option>
                          <option value="carousel">Carousel</option>
                          <option value="text">Text Only</option>
                        </select>
                      </div>

                      {/* Status Filter */}
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] uppercase font-bold text-zinc-500">Status:</span>
                        <select
                          className="bg-[#0B0C0F] border border-[#242832] rounded-lg px-3 py-1.5 text-xs text-zinc-300 focus:outline-none"
                          value={statusFilter}
                          onChange={e => setStatusFilter(e.target.value)}
                        >
                          <option value="all">All Status</option>
                          <option value="active">Active Now</option>
                          <option value="paused">Paused / Past</option>
                        </select>
                      </div>
                    </div>

                    {/* Sorting dropdown */}
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] uppercase font-bold text-zinc-500">Sort By:</span>
                      <select
                        className="bg-[#0B0C0F] border border-[#242832] rounded-lg px-3 py-1.5 text-xs text-zinc-300 focus:outline-none"
                        value={sortBy}
                        onChange={e => setSortBy(e.target.value)}
                      >
                        <option value="longevity">Longevity (Scale proxy)</option>
                        <option value="newest">Newest Launch</option>
                        <option value="oldest">Oldest Tracked</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Ads Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                  {filteredAds.map(ad => {
                    const comp = competitors.find(c => c.id === ad.competitorId);
                    return (
                      <div
                        key={ad.id}
                        className="bg-[#050506] border border-[#242832] hover:border-zinc-700 rounded-3xl overflow-hidden flex flex-col h-[400px] shadow-lg group relative"
                      >
                        {/* Ad Card Header (Competitor & Platform) */}
                        <div className="p-4 border-b border-[#242832] flex items-center justify-between bg-zinc-950/40">
                          <div className="flex items-center gap-2">
                            <div className={`px-2 py-0.5 rounded text-[10px] font-bold text-white bg-gradient-to-br ${comp?.logoColor}`}>
                              {comp?.name}
                            </div>
                            <span className="text-[10px] uppercase tracking-wider text-zinc-500">
                              {ad.platform}
                            </span>
                          </div>

                          <div className="flex items-center gap-2">
                            {ad.status === 'active' ? (
                              <span className="h-2 w-2 rounded-full bg-green-500"></span>
                            ) : (
                              <span className="h-2 w-2 rounded-full bg-red-500"></span>
                            )}
                            <span className="text-[9px] uppercase font-bold tracking-widest text-zinc-400">
                              {ad.status}
                            </span>
                          </div>
                        </div>

                        {/* Ad Thumbnail Preview (Mock HTML visual) */}
                        <div className={`h-40 bg-gradient-to-br ${ad.thumbnailGradient} relative flex items-center justify-center p-6 border-b border-[#242832] group-hover:brightness-110 transition-all`}>
                          {/* Platform Logo Overlay */}
                          <div className="absolute top-3 right-3 text-zinc-500 text-xs font-bold uppercase tracking-wider bg-zinc-950/80 px-2.5 py-1 rounded-md border border-[#242832]">
                            {ad.format}
                          </div>

                          {/* Interactive mock creative visual */}
                          <div className="text-center space-y-2 z-10 select-none">
                            <p className="text-[10px] uppercase tracking-widest font-bold text-[#2F6BFF]">{comp?.name}</p>
                            <h4 className="text-sm font-semibold max-w-xs text-white line-clamp-2 px-2">{ad.title}</h4>
                            {ad.format === 'video' && (
                              <div className="mx-auto w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 flex items-center justify-center cursor-pointer shadow-lg mt-2">
                                <SafeIcon name="Play" className="w-4 h-4 text-white fill-white ml-0.5" />
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Ad Copy snippet */}
                        <div className="p-5 flex-grow flex flex-col justify-between">
                          <div>
                            <p className="text-xs text-zinc-400 font-light leading-relaxed line-clamp-3">
                              {ad.copy}
                            </p>
                          </div>

                          <div className="mt-4 pt-4 border-t border-zinc-950 flex justify-between items-center">
                            <div>
                              <p className="text-[8px] font-bold text-zinc-500 uppercase tracking-widest">Ad Longevity</p>
                              <p className="text-xs font-bold text-[#2F6BFF] mt-0.5">{ad.longevityDays} Days Active</p>
                            </div>

                            <button
                              onClick={() => {
                                setSelectedAd(ad);
                                setAdTab('copy');
                              }}
                              className="text-[10px] uppercase font-bold tracking-widest text-zinc-400 group-hover:text-white flex items-center gap-1 transition-colors"
                            >
                              View AI Analysis
                              <FiArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {filteredAds.length === 0 && (
                    <div className="col-span-full bg-[#050506] border border-[#242832] rounded-3xl p-16 text-center">
                      <FiAlertCircle className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                      <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-2">No Matching Ads Found</h4>
                      <p className="text-xs text-zinc-500 max-w-sm mx-auto leading-relaxed">
                        Try clearing your search query or adjusting your channel filters to view matching competitor ad records.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* TAB 3: WHITELABEL REPORT BUILDER */}
          {activeTab === 'reports' && (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-8"
            >
              {/* Header */}
              <div className="flex justify-between items-start md:items-center flex-col md:flex-row gap-4 print-hidden">
                <div>
                  <h1 className="text-3xl font-serif font-bold text-white">Whitelabel Report Builder</h1>
                  <p className="text-xs text-zinc-500 mt-1">Export customized competitor creative briefs featuring your agency logo and branding.</p>
                </div>
                
                <button
                  onClick={() => window.print()}
                  className="bg-[#2F6BFF] hover:bg-[#1a54ff] text-white px-6 py-4 rounded-xl text-xs font-bold uppercase tracking-widest flex items-center gap-2 shadow-lg shadow-blue-600/15"
                >
                  <FiDownload className="w-4 h-4" />
                  Export / Print PDF
                </button>
              </div>

              {/* Builder interface */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* Control Panel (left) */}
                <div className="lg:col-span-5 bg-[#050506] border border-[#242832] rounded-3xl p-6 space-y-6 shadow-xl print-hidden">
                  <h3 className="text-xs font-bold text-white uppercase tracking-widest border-b border-[#242832] pb-3">Report Branding Parameters</h3>

                  {/* Competitor select */}
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider ml-0.5">Competitor Data Target</label>
                    <select
                      className="input-field"
                      value={reportCompetitorId}
                      onChange={e => setReportCompetitorId(e.target.value)}
                    >
                      {competitors.map(comp => (
                        <option key={comp.id} value={comp.id}>
                          {comp.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Agency Name */}
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider ml-0.5">Agency / Presenter Name</label>
                    <input
                      type="text"
                      className="input-field"
                      value={agencyName}
                      onChange={e => setAgencyName(e.target.value)}
                      placeholder="e.g. Maxmark Agency"
                    />
                  </div>

                  {/* Report Title */}
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider ml-0.5">Report Header Title</label>
                    <input
                      type="text"
                      className="input-field"
                      value={reportTitle}
                      onChange={e => setReportTitle(e.target.value)}
                      placeholder="e.g. Competitive Advertising Audit"
                    />
                  </div>

                  {/* Primary Color Picker */}
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider ml-0.5">Brand Accent Color</label>
                    <div className="flex gap-3 items-center">
                      <input
                        type="color"
                        className="w-12 h-12 bg-transparent border border-[#242832] rounded cursor-pointer"
                        value={brandColor}
                        onChange={e => setBrandColor(e.target.value)}
                      />
                      <input
                        type="text"
                        className="input-field max-w-[140px]"
                        value={brandColor}
                        onChange={e => setBrandColor(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Custom Logo Upload */}
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider ml-0.5">Custom Agency Logo</label>
                    <div className="border border-dashed border-[#242832] hover:border-zinc-700 bg-[#0B0C0F] rounded-xl p-4 text-center cursor-pointer transition-all relative">
                      <input
                        type="file"
                        accept="image/*"
                        className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                        onChange={handleLogoChange}
                      />
                      {uploadedLogo ? (
                        <div className="flex items-center justify-center gap-2">
                          <img src={uploadedLogo} alt="Preview" className="h-8 max-w-[150px] object-contain" />
                          <button 
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setUploadedLogo(null); }}
                            className="text-red-500 hover:text-red-400 text-[10px] font-bold uppercase tracking-widest ml-4 z-10"
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-1 text-zinc-500">
                          <FiLayout className="w-5 h-5 mx-auto text-zinc-600" />
                          <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">Click to Upload Logo</p>
                          <p className="text-[9px] font-light">Supports PNG, JPG (Max 2MB)</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Executive recommendations */}
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider ml-0.5">Executive Summary & Recommendations</label>
                    <textarea
                      rows={5}
                      className="input-field resize-none text-xs"
                      value={customComments}
                      onChange={e => setCustomComments(e.target.value)}
                    />
                  </div>
                </div>

                {/* Live Report Preview (right) */}
                <div className="lg:col-span-7 bg-[#050506] border border-[#242832] rounded-3xl p-8 shadow-2xl relative overflow-hidden flex flex-col justify-between min-h-[700px]">
                  
                  {/* Outer preview wrapping block */}
                  <div id="print-area" className="bg-white text-zinc-900 p-10 rounded-2xl flex-grow shadow flex flex-col justify-between border border-zinc-200">
                    
                    {/* Report Header */}
                    <div>
                      <div className="flex justify-between items-start border-b-2 pb-6 mb-8" style={{ borderColor: brandColor }}>
                        <div>
                          <h2 className="text-xl font-bold tracking-tight text-zinc-950">{reportTitle}</h2>
                          <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mt-1">Target Competitor: {competitors.find(c => c.id === reportCompetitorId)?.name}</p>
                        </div>
                        {uploadedLogo ? (
                          <img src={uploadedLogo} alt="Logo" className="h-10 max-w-[160px] object-contain" />
                        ) : (
                          <div className="text-right">
                            <span className="text-xs font-black tracking-tighter" style={{ color: brandColor }}>{agencyName.toUpperCase()}</span>
                            <p className="text-[8px] text-zinc-500 uppercase tracking-widest font-bold mt-0.5">Ad Intelligence Report</p>
                          </div>
                        )}
                      </div>

                      {/* Executive summary section */}
                      <div className="mb-8">
                        <h4 className="text-xs uppercase font-extrabold tracking-widest text-zinc-500 mb-2">Executive Summary</h4>
                        <div className="p-5 rounded-lg text-xs leading-relaxed text-zinc-700 bg-zinc-50 border border-zinc-150 border-l-4" style={{ borderLeftColor: brandColor }}>
                          {customComments}
                        </div>
                      </div>

                      {/* Creative samples list */}
                      <div>
                        <h4 className="text-xs uppercase font-extrabold tracking-widest text-zinc-500 mb-4">Competitor Creative Database Sample</h4>
                        <div className="space-y-4">
                          {ads.filter(ad => ad.competitorId === reportCompetitorId).map(ad => (
                            <div key={ad.id} className="p-4 border border-zinc-200 rounded-lg bg-zinc-50 flex gap-4">
                              <div className="w-1/3 flex flex-col justify-between shrink-0">
                                <div>
                                  <span className="text-[9px] uppercase font-black px-1.5 py-0.5 rounded bg-zinc-900 text-white select-none">
                                    {ad.platform}
                                  </span>
                                  <h5 className="text-[11px] font-bold text-zinc-950 mt-2 leading-tight">{ad.title}</h5>
                                </div>

                                <div className="mt-4">
                                  <p className="text-[8px] uppercase tracking-widest text-zinc-400 font-bold">Longevity Scale</p>
                                  <p className="text-xs font-black" style={{ color: brandColor }}>{ad.longevityDays} Days Active</p>
                                </div>
                              </div>

                              <div className="w-2/3 border-l border-zinc-200 pl-4 space-y-3">
                                <div>
                                  <p className="text-[8px] font-bold text-zinc-500 uppercase tracking-wider">Ad Copy Body</p>
                                  <p className="text-[10px] text-zinc-600 font-light mt-0.5 leading-relaxed italic">"{ad.copy}"</p>
                                </div>
                                <div className="grid grid-cols-2 gap-2 text-[9px] text-zinc-500">
                                  <div>
                                    <span className="font-bold text-zinc-700 block">Copy Hook:</span>
                                    <span className="line-clamp-2">{ad.analysis.hook}</span>
                                  </div>
                                  <div>
                                    <span className="font-bold text-zinc-700 block">Offer details:</span>
                                    <span className="line-clamp-2">{ad.analysis.funnel}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Report Footer */}
                    <div className="mt-12 pt-6 border-t border-zinc-150 flex justify-between items-center text-[9px] text-zinc-400">
                      <p>Prepared by {agencyName} • Powered by Maxmark Client Intelligence Portal</p>
                      <p>Date Generated: {new Date().toLocaleDateString()}</p>
                    </div>

                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* TAB 4: REQUEST COMPETITOR MONITOR */}
          {activeTab === 'request' && (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="grid grid-cols-1 lg:grid-cols-12 gap-8"
            >
              {/* Form container */}
              <div className="lg:col-span-7 bg-[#050506] border border-[#242832] rounded-3xl p-6 sm:p-8 shadow-2xl space-y-6">
                <div>
                  <h1 className="text-2xl font-serif font-bold text-white">Request Competitor Surveillance</h1>
                  <p className="text-xs text-zinc-500 mt-1">Submit new target websites for tracking. Processing usually takes 24-48 hours.</p>
                </div>

                {reqSubmitSuccess && (
                  <div className="p-4 bg-green-950/20 border border-green-500/20 text-green-400 text-xs rounded-xl flex items-center gap-3">
                    <FiCheckCircle className="w-5 h-5 shrink-0 text-green-400" />
                    <div>
                      <p className="font-bold">Surveillance Request Queued</p>
                      <p className="text-[10px] mt-0.5 font-light">Maxmark crawlers are auditing the website. Active monitoring starts shortly.</p>
                    </div>
                  </div>
                )}

                <form onSubmit={handleRequestSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider ml-0.5">Competitor Brand Name</label>
                      <input
                        type="text"
                        className="input-field"
                        placeholder="e.g. PiggyVest"
                        value={reqName}
                        onChange={e => setReqName(e.target.value)}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider ml-0.5">Competitor Website URL</label>
                      <input
                        type="url"
                        className="input-field"
                        placeholder="https://piggyvest.com"
                        value={reqWebsite}
                        onChange={e => setReqWebsite(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider ml-0.5">Their Primary Competitors (Optional)</label>
                    <input
                      type="text"
                      className="input-field"
                      placeholder="e.g. Cowrywise, Risevest"
                      value={reqCompetitors}
                      onChange={e => setReqCompetitors(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider ml-0.5">Your Core Competitive Concerns / Target Products</label>
                    <textarea
                      rows={4}
                      className="input-field resize-none text-xs"
                      placeholder="What specific offers, products, or campaigns do you want us to focus on? (e.g. savings interest rate ads, mobile wallet signup angles)"
                      value={reqConcern}
                      onChange={e => setReqConcern(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2.5">
                    <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider ml-0.5">Advertising Channels to Track</label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {['Meta (Facebook/Instagram)', 'Google Ads', 'TikTok Ads', 'LinkedIn Ads'].map(ch => {
                        const isChecked = reqChannels.includes(ch);
                        return (
                          <label
                            key={ch}
                            className={`flex items-center gap-3 p-3 rounded-xl border text-xs font-bold uppercase tracking-widest cursor-pointer transition-colors ${
                              isChecked 
                                ? 'bg-blue-600/10 border-blue-600 text-white' 
                                : 'bg-[#0B0C0F] border-[#242832] text-zinc-500 hover:border-zinc-700'
                            }`}
                          >
                            <input
                              type="checkbox"
                              className="hidden"
                              checked={isChecked}
                              onChange={() => {
                                if (isChecked) {
                                  setReqChannels(reqChannels.filter(c => c !== ch));
                                } else {
                                  setReqChannels([...reqChannels, ch]);
                                }
                              }}
                            />
                            {ch.split(' ')[0]}
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={reqSubmitLoading}
                    className="w-full bg-[#2F6BFF] hover:bg-[#1a54ff] disabled:opacity-50 text-white py-4 rounded-xl font-bold uppercase tracking-widest text-xs transition-all shadow-lg shadow-blue-600/20 mt-4"
                  >
                    {reqSubmitLoading ? 'Configuring Crawlers...' : 'Initiate Surveillance'}
                  </button>
                </form>
              </div>

              {/* Status listing (right) */}
              <div className="lg:col-span-5 space-y-6">
                {/* Information Card */}
                <div className="bg-[#050506] border border-[#242832] rounded-3xl p-6 shadow-xl space-y-4">
                  <h3 className="text-xs font-bold text-white uppercase tracking-widest">How surveillance works</h3>
                  <div className="space-y-4 text-xs text-zinc-400 font-light leading-relaxed">
                    <p>
                      <strong>1. Website Verification:</strong> Maxmark crawlers audit target domains to verify technical stack, landing page patterns, and meta elements.
                    </p>
                    <p>
                      <strong>2. Channel Scraper Hooking:</strong> We search platform ad transparency libraries (Meta, Google, TikTok, LinkedIn) to catalog current active ad records.
                    </p>
                    <p>
                      <strong>3. Database Seeding & Alerts:</strong> The target is added to your workspace. Daily automated scans evaluate ad pauses, launches, and duration longevity.
                    </p>
                  </div>
                </div>

                {/* Queue status */}
                <div className="bg-[#050506] border border-[#242832] rounded-3xl p-6 shadow-xl space-y-4">
                  <h3 className="text-xs font-bold text-white uppercase tracking-widest">Pending Requests Queue ({pendingRequests.length})</h3>
                  
                  <div className="space-y-3 max-h-60 overflow-y-auto pr-1">
                    {pendingRequests.map(req => (
                      <div key={req.id} className="p-4 bg-zinc-950 border border-zinc-900 rounded-2xl flex justify-between items-center">
                        <div>
                          <h4 className="text-xs font-bold text-white">{req.competitor_name}</h4>
                          {req.competitor_websites && (
                            <a href={req.competitor_websites} target="_blank" rel="noopener noreferrer" className="text-[10px] text-zinc-500 hover:text-blue-500 flex items-center gap-1 mt-0.5">
                              {req.competitor_websites.replace(/^https?:\/\//i, '')}
                              <FiExternalLink className="w-2.5 h-2.5" />
                            </a>
                          )}
                        </div>
                        <div className="text-right">
                          <span className={`inline-block px-2.5 py-1 text-[8px] font-bold uppercase rounded border ${
                            req.status === 'converted'
                              ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                              : 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20'
                          }`}>
                            {req.status === 'converted' ? 'tracking' : req.status}
                          </span>
                          <p className="text-[8px] text-zinc-600 mt-1">{new Date(req.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                    ))}

                    {pendingRequests.length === 0 && (
                      <div className="p-6 text-center text-zinc-600 text-xs font-light">
                        No pending competitor crawlers. Use the form to request tracker activation.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* TAB 5: PORTAL SETTINGS */}
          {activeTab === 'settings' && (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-4xl space-y-8"
            >
              <div>
                <h1 className="text-2xl font-serif font-bold text-white">Client Workspace Settings</h1>
                <p className="text-xs text-zinc-500 mt-1">Manage notification alerts, team access, and your Maxmark intelligence plan.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Left Card: Account & Plan details */}
                <div className="bg-[#050506] border border-[#242832] rounded-3xl p-6 sm:p-8 shadow-2xl space-y-6">
                  <h3 className="text-xs font-bold text-white uppercase tracking-widest border-b border-[#242832] pb-3">Subscription & Quota</h3>

                  <div className="bg-gradient-to-br from-blue-600/10 via-zinc-950 to-indigo-900/10 border border-blue-500/20 p-5 rounded-2xl space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-blue-400">Workspace</span>
                      <span className="bg-blue-600 text-white text-[9px] font-bold uppercase px-2 py-0.5 rounded">Active</span>
                    </div>
                    <h4 className="text-lg font-bold text-white">{clientUser?.company_name}</h4>
                    <p className="text-xs text-zinc-400 font-light">
                      {competitors.length} competitor{competitors.length === 1 ? '' : 's'} currently tracked, with {ads.length} archived ad{ads.length === 1 ? '' : 's'} in your swipe file.
                    </p>
                  </div>

                  <div className="space-y-3 pt-2">
                    <h4 className="text-xs font-bold text-white uppercase tracking-widest">Need to Adjust Your Plan?</h4>
                    <p className="text-[10px] text-zinc-500 font-light leading-relaxed">
                      Reach out to your Maxmark account team to add monitors or change your subscription.
                    </p>
                    <a
                      href="mailto:info@maxmarkagency.com"
                      className="flex items-center gap-3 p-3 bg-zinc-950 rounded-xl border border-zinc-900 hover:border-zinc-700 transition-colors"
                    >
                      <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-blue-600 to-indigo-700 flex items-center justify-center font-bold text-xs text-white">
                        <FiSettings className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-white">Contact Maxmark Support</p>
                        <p className="text-[10px] text-zinc-500">info@maxmarkagency.com</p>
                      </div>
                    </a>
                  </div>

                  <button
                    onClick={() => setActiveTab('request')}
                    className="w-full bg-zinc-900 hover:bg-zinc-800 border border-[#242832] text-white py-3 rounded-xl font-bold uppercase tracking-widest text-[10px] transition-all flex items-center justify-center gap-2"
                  >
                    <FiPlusCircle className="w-3.5 h-3.5 text-blue-400" />
                    Request Additional Competitor Track
                  </button>
                </div>

                {/* Right Card: Notification & Team Access */}
                <div className="bg-[#050506] border border-[#242832] rounded-3xl p-6 sm:p-8 shadow-2xl space-y-6">
                  <h3 className="text-xs font-bold text-white uppercase tracking-widest border-b border-[#242832] pb-3">Delivery Preferences</h3>

                  <div className="space-y-4">
                    <h4 className="text-xs font-bold text-zinc-300 uppercase tracking-wider">Alert Notifications</h4>
                    
                    <div className="space-y-3">
                      {[
                        { key: 'new_campaigns', title: 'New Competitor Campaign Launches', desc: 'Email digest as soon as competitors launch fresh creative angles.' },
                        { key: 'longevity_alerts', title: 'Ad Scale & Longevity Alerts', desc: 'Alert when a competitor creative remains active past 45 days (scale proxy).' },
                        { key: 'monthly_briefing', title: 'Monthly Maxmark Executive Briefing', desc: 'Receive synthesized PDF intelligence audits directly in your inbox.' }
                      ].map((set) => (
                        <label key={set.key} className="flex gap-4 items-start cursor-pointer group">
                          <input
                            type="checkbox"
                            checked={!!preferences[set.key]}
                            onChange={() => handleTogglePreference(set.key)}
                            className="mt-1 rounded border-[#242832] bg-[#0B0C0F] text-[#2F6BFF] focus:ring-0 focus:ring-offset-0"
                          />
                          <div>
                            <p className="text-xs font-bold text-zinc-300 group-hover:text-white transition-colors">{set.title}</p>
                            <p className="text-[10px] text-zinc-500 mt-0.5 font-light leading-relaxed">{set.desc}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3 pt-4 border-t border-zinc-900">
                    <h4 className="text-xs font-bold text-white uppercase tracking-widest">Team Portal Access</h4>
                    <p className="text-[10px] text-zinc-500 font-light leading-relaxed">
                      Need a colleague added to this workspace? Email your Maxmark account team and we'll set them up.
                    </p>
                    <a
                      href="mailto:info@maxmarkagency.com?subject=Add%20a%20teammate%20to%20our%20portal"
                      className="inline-block bg-zinc-900 hover:bg-zinc-800 border border-[#242832] text-white px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-colors"
                    >
                      Request Team Access
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

        </main>

        {/* Swipe File Ad Detail Slide-over Panel */}
        <AnimatePresence>
          {selectedAd && (
            <div className="fixed inset-0 z-50 overflow-hidden flex justify-end print-hidden">
              {/* Backdrop */}
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setSelectedAd(null)}
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              />

              {/* Panel Container */}
              <motion.div
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'tween', duration: 0.3 }}
                className="relative w-full max-w-xl bg-[#050506] border-l border-[#242832] h-full flex flex-col shadow-2xl z-10"
              >
                {/* Header */}
                <div className="p-6 border-b border-[#242832] flex items-center justify-between bg-zinc-950">
                  <div>
                    <span className="text-[9px] uppercase font-bold tracking-widest text-[#2F6BFF]">Ad Creative Analysis</span>
                    <h3 className="text-lg font-serif font-bold text-white mt-1">
                      {competitors.find(c => c.id === selectedAd.competitorId)?.name} • {selectedAd.id.toUpperCase()}
                    </h3>
                  </div>

                  <button 
                    onClick={() => setSelectedAd(null)}
                    className="p-2 rounded-lg text-zinc-500 hover:text-white hover:bg-zinc-900 transition-colors"
                  >
                    <FiX className="w-5 h-5" />
                  </button>
                </div>

                {/* Content Area */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                  {/* Creative Preview */}
                  <div className={`h-48 bg-gradient-to-br ${selectedAd.thumbnailGradient} rounded-2xl flex items-center justify-center p-6 border border-[#242832]`}>
                    <div className="text-center space-y-2">
                      <span className="px-2.5 py-1 text-[9px] font-bold uppercase rounded bg-zinc-950/80 text-[#A7ADB8] border border-[#242832]">
                        {selectedAd.platform.toUpperCase()} • {selectedAd.format.toUpperCase()}
                      </span>
                      <h4 className="text-base font-semibold text-white px-4">{selectedAd.title}</h4>
                    </div>
                  </div>

                  {/* Metadata Row */}
                  <div className="grid grid-cols-3 gap-4 bg-zinc-950 border border-zinc-900 p-4 rounded-2xl text-center">
                    <div>
                      <span className="text-[8px] font-bold text-zinc-500 uppercase tracking-widest">First Detected</span>
                      <p className="text-xs font-bold text-white mt-1">{selectedAd.firstSeen}</p>
                    </div>
                    <div>
                      <span className="text-[8px] font-bold text-zinc-500 uppercase tracking-widest">Active Duration</span>
                      <p className="text-xs font-bold text-[#2F6BFF] mt-1">{selectedAd.longevityDays} Days</p>
                    </div>
                    <div>
                      <span className="text-[8px] font-bold text-zinc-500 uppercase tracking-widest">Current Status</span>
                      <p className="text-xs font-bold text-white mt-1 flex items-center justify-center gap-1.5">
                        <span className={`w-1.5 h-1.5 rounded-full ${selectedAd.status === 'active' ? 'bg-green-500' : 'bg-red-500'}`}></span>
                        {selectedAd.status.toUpperCase()}
                      </p>
                    </div>
                  </div>

                  {/* Body Copy Section */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Ad Copy Text</span>
                      <button
                        onClick={() => handleCopyText(selectedAd.copy, 'copy')}
                        className="text-[10px] text-zinc-500 hover:text-white flex items-center gap-1 transition-colors"
                      >
                        {copiedId === 'copy' ? 'Copied' : <><FiCopy className="w-3 h-3" /> Copy Copy</>}
                      </button>
                    </div>
                    <div className="bg-[#0B0C0F] border border-[#242832] p-4 rounded-xl text-xs leading-relaxed text-zinc-300 whitespace-pre-wrap select-text font-light">
                      "{selectedAd.copy}"
                    </div>
                  </div>

                  {/* Tabbed Analysis Breakdown */}
                  <div className="space-y-4">
                    {/* Tabs Navigation */}
                    <div className="flex border-b border-[#242832] gap-2">
                      {[
                        { id: 'copy', label: 'AI Copy Analysis' },
                        { id: 'funnel', label: 'Funnel & Offer' },
                        { id: 'advisory', label: 'Maxmark Advisory' }
                      ].map(tab => (
                        <button
                          key={tab.id}
                          onClick={() => setAdTab(tab.id)}
                          className={`pb-3.5 px-3 text-xs font-bold uppercase tracking-wider relative transition-colors ${
                            adTab === tab.id ? 'text-[#2F6BFF]' : 'text-[#A7ADB8] hover:text-white'
                          }`}
                        >
                          {tab.label}
                          {adTab === tab.id && (
                            <motion.div 
                              layoutId="panelTabIndicator"
                              className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#2F6BFF]"
                            />
                          )}
                        </button>
                      ))}
                    </div>

                    {/* Tabs Content */}
                    <div className="text-xs leading-relaxed text-zinc-300 font-light space-y-4">
                      {adTab === 'copy' && (
                        <div className="space-y-4 bg-zinc-950/60 p-5 rounded-2xl border border-zinc-900">
                          <div>
                            <span className="text-[10px] font-bold uppercase text-zinc-500 block mb-1">Hook Mechanics:</span>
                            <p>{selectedAd.analysis.hook}</p>
                          </div>
                          <div>
                            <span className="text-[10px] font-bold uppercase text-zinc-500 block mb-1">Persuasion Angle:</span>
                            <p>{selectedAd.analysis.angle}</p>
                          </div>
                        </div>
                      )}

                      {adTab === 'funnel' && (
                        <div className="space-y-4 bg-zinc-950/60 p-5 rounded-2xl border border-zinc-900">
                          <div>
                            <span className="text-[10px] font-bold uppercase text-zinc-500 block mb-1">Destination Landing Page:</span>
                            <a 
                              href={selectedAd.landingPageUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-[#2F6BFF] hover:underline flex items-center gap-1.5 mt-0.5 font-bold"
                            >
                              {selectedAd.landingPageUrl}
                              <FiExternalLink className="w-3.5 h-3.5" />
                            </a>
                          </div>
                          <div>
                            <span className="text-[10px] font-bold uppercase text-zinc-500 block mb-1">Offer Model:</span>
                            <p>{selectedAd.analysis.funnel}</p>
                          </div>
                        </div>
                      )}

                      {adTab === 'advisory' && (
                        <div className="space-y-4 bg-zinc-950/60 p-5 rounded-2xl border border-zinc-900">
                          <div>
                            <span className="text-[10px] font-bold uppercase text-zinc-500 block mb-1">Tactical Response Plan:</span>
                            <p className="text-zinc-300 bg-blue-900/10 p-3 rounded-lg border border-blue-500/10 border-l-2 border-l-[#2F6BFF]">
                              {selectedAd.analysis.maxmarkAdvisory}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
